/**
 * Times a frame's GPU work per render step with `EXT_disjoint_timer_query_webgl2`. WebGL runs one
 * `TIME_ELAPSED` query at a time, so a nested step ends its parent's query and starts its own: each
 * query times one segment of one step's own work, and the frame's segments sum to its GPU time.
 */
const TIMER_EXTENSION = 'EXT_disjoint_timer_query_webgl2';
/**
 * A driver resolves a query two or three frames on. A frame still unresolved after this many has
 * lost its results, so its queries are deleted, never reused.
 */
const MAX_PENDING_FRAMES = 10;
export class GpuTimer {
    gl;
    ext;
    running = null;
    frame = [];
    /** Closed frames whose results have not all arrived, oldest first. */
    pending = [];
    pool = [];
    /** Null when the context offers no timer query. */
    static create(gl) {
        const ext = gl.getExtension(TIMER_EXTENSION);
        return ext ? new GpuTimer(gl, ext) : null;
    }
    constructor(gl, ext) {
        this.gl = gl;
        this.ext = ext;
    }
    /** Ends the running segment and starts one for `step`. A lost context times nothing. */
    switchTo(step) {
        this.endSegment();
        // A lost context's `createQuery` returns null, which `beginQuery` refuses with a TypeError.
        if (this.gl.isContextLost())
            return;
        const query = this.pool.pop() ?? this.gl.createQuery();
        this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
        this.running = { step, query };
        this.frame.push(this.running);
    }
    endFrame() {
        this.endSegment();
        if (this.frame.length === 0)
            return;
        this.pending.push(this.frame);
        this.frame = [];
        if (this.pending.length > MAX_PENDING_FRAMES)
            this.delete(this.pending.shift());
    }
    /**
     * Hands each segment of every frame whose results have all arrived to `onSegment`, oldest first,
     * and answers how many frames that was. A disjoint operation, such as a clock change, voids every
     * result in flight, so those frames are dropped. A lost context voids every query it made, so
     * the pool empties too and a restored context gets new queries.
     */
    collect(onSegment) {
        if (this.gl.isContextLost()) {
            this.pending.length = 0;
            this.pool.length = 0;
            return 0;
        }
        if (this.gl.getParameter(this.ext.GPU_DISJOINT_EXT)) {
            for (const frame of this.pending.splice(0))
                this.recycle(frame);
            return 0;
        }
        let frames = 0;
        while (this.pending.length > 0 && this.hasArrived(this.pending[0])) {
            const frame = this.pending.shift();
            for (const { step, query } of frame) {
                onSegment(step, this.gl.getQueryParameter(query, this.gl.QUERY_RESULT));
            }
            this.recycle(frame);
            frames += 1;
        }
        return frames;
    }
    dispose() {
        this.endSegment();
        for (const frame of [this.frame, ...this.pending])
            this.recycle(frame);
        this.frame = [];
        this.pending.length = 0;
        for (const query of this.pool.splice(0))
            this.gl.deleteQuery(query);
    }
    endSegment() {
        if (!this.running)
            return;
        this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
        this.running = null;
    }
    /** Queries resolve in order, so a frame whose last query has resolved has resolved whole. */
    hasArrived(frame) {
        return this.gl.getQueryParameter(frame.at(-1).query, this.gl.QUERY_RESULT_AVAILABLE);
    }
    recycle(frame) {
        for (const { query } of frame)
            this.pool.push(query);
    }
    delete(frame) {
        for (const { query } of frame)
            this.gl.deleteQuery(query);
    }
}
//# sourceMappingURL=gpuTimer.js.map